import {
  WorkerCallAction,
  WorkerInitAction,
  WorkerResponseType,
  WorkerTerminateAction
} from "../../node_modules/@zenid/websdk/dist/core/internal/worker-interfaces.js";
import { zenidLog } from "../../node_modules/@zenid/websdk/dist/core/zenid-log.js";
// For native function names
export var NativeFunction;
(function (NativeFunction) {
  NativeFunction["ProcessFrame"] = "nativeProcessFrame";
  NativeFunction["SetVisualizerVersion"] = "nativeSetVisualizerVersion";
  NativeFunction["InitOrRestart"] = "nativeInitOrRestart";
  NativeFunction["RestartWithPreviousSettings"] =
    "nativeRestartWithPreviousSettings";
  NativeFunction["GetRenderCommands"] = "nativeGetRenderCommands";
  NativeFunction["ReadyForFrame"] = "nativeReadyForFrame";
  NativeFunction["GetStateContainerJson"] = "nativeGetStateContainerJson";
  NativeFunction["GetFrameProcessingResolution"] =
    "nativeGetFrameProcessingResolution";
  NativeFunction["SubmitExternalResponse"] = "nativeSubmitExternalResponse";
  NativeFunction["GetUploadReadyData"] = "nativeGetUploadReadyData";
  NativeFunction["SetDebugVisualization"] = "nativeSetDebugVisualization";
  NativeFunction["SetLanguage"] = "nativeSetLanguage";
  NativeFunction["GetLanguageLocale"] = "nativeGetLanguageLocale";
  NativeFunction["SetupResourceLoader"] = "nativeSetupResourceLoader";
  NativeFunction["Authorize"] = "nativeAuthorize";
  NativeFunction["GetChallengeToken"] = "nativeGetChallengeToken";
  NativeFunction["SelectProfile"] = "nativeSelectProfile";
  NativeFunction["Unload"] = "nativeUnload";
  NativeFunction["GetSdkWizardSteps"] = "nativeGetSdkWizardSteps";
  NativeFunction["MeasureAvailableRAM"] = "nativePrepareAndEstimateMemory";
  NativeFunction["GetSupportedPageCount"] = "nativeGetSupportedPageCount";
})(NativeFunction || (NativeFunction = {}));
export var DataType;
(function (DataType) {
  DataType["object"] = "object";
  DataType["string"] = "string";
})(DataType || (DataType = {}));
export class ZenidSDK {
  worker;
  callbackMap;
  callIdCounter;
  initialized = false;
  initPromise;
  useSingleThread = null;
  // Global debug state accessible by verifiers
  debugVisualizationEnabled = false;
  // Transform ZenidSDK to singleton pattern
  static instance;
  static get Instance() {
    if (!ZenidSDK.instance) {
      ZenidSDK.instance = new ZenidSDK();
    }
    return ZenidSDK.instance;
  }
  constructor() {
    this.worker = null;
    this.callbackMap = new Map();
    this.callIdCounter = 0;
    this.initPromise = null;
  }
  /**
   * Initialize the SDK
   * @returns {Promise<void>} Resolves when SDK is initialized
   */
  setup(resourcePath, useSingleThreaded) {
    if (this.initPromise) {
      return this.initPromise;
    }
    this.initPromise = new Promise(async (resolve, reject) => {
      try {
        // Determine the SDK base path from the current module's URL
        // This works because this file is always at dist/core/internal/zenid-sdk.js
        const currentScriptUrl =
          window.location.origin + "/zenid-sdk/core/internal/zenid-sdk.js";
        const sdkBasePath = currentScriptUrl.substring(
          0,
          currentScriptUrl.lastIndexOf("/core/internal/")
        );
        // Create the worker with absolute URL
        const workerName = useSingleThreaded
          ? "zenid-worker-st.js"
          : "zenid-worker.js";
        this.worker = new Worker(`${sdkBasePath}/core/internal/${workerName}`);
        this.useSingleThread = useSingleThreaded;
        // register initialized, but set initialized to true in resolve
        this.callbackMap.set(WorkerResponseType.Initialized, {
          resolve: async () => {
            this.initialized = true;
            await this.callNative(
              NativeFunction.SetupResourceLoader,
              resourcePath
            );
            resolve();
          },
          reject: error => {
            this.initialized = false;
            reject(error);
          }
        });
        // Set up message handling
        this.worker.onmessage = e => this.handleWorkerMessage(e);
        // Call init function
        this.worker.postMessage(new WorkerInitAction());
      } catch (error) {
        reject(error);
      }
    });
    return this.initPromise;
  }
  /**
   * Generic method to call any native function
   * @param {string} functionName - Name of the native function to call
   * @param {...any} args - Arguments to pass to the function
   * @returns {Promise<any>} Resolves with the result of the function call
   */
  callNative(functionName, ...args) {
    if (!this.initialized) {
      return Promise.reject(
        new Error("SDK not initialized. Call init() first.")
      );
    }
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error("Worker not initialized"));
        return;
      }
      const callId = this.getNextCallId();
      const transferables = this.findTransferables(args);
      // Register callback
      this.registerCallback(callId, resolve, reject);
      // Call the function
      const callMessage = new WorkerCallAction(
        callId,
        functionName,
        args,
        transferables
      );
      this.worker.postMessage(callMessage, transferables);
    });
  }
  /**
   * Finds ArrayBuffers that can be transferred instead of copied
   * @param {Array} args - Arguments to search
   * @returns {Array<ArrayBuffer>} - Transferable objects
   */
  findTransferables(args) {
    const transferables = [];
    const findBuffer = obj => {
      if (!obj) return;
      if (obj instanceof ArrayBuffer) {
        transferables.push(obj);
      } else if (ArrayBuffer.isView(obj) && obj.buffer) {
        transferables.push(obj.buffer);
      } else if (typeof obj === DataType.object) {
        for (const key in obj) {
          findBuffer(obj[key]);
        }
      }
    };
    args.forEach(findBuffer);
    return transferables;
  }
  /**
   * Handle messages from the worker
   * @param {MessageEvent<WorkerResponse>} e - Message event
   */
  handleWorkerMessage(e) {
    const { type, id, result, error } = e.data;
    // zenidLog.debug("ZenidSDK: Received worker message:", JSON.stringify(e.data));
    if (type === WorkerResponseType.Initialized) {
      const callbacks = this.callbackMap.get(WorkerResponseType.Initialized);
      if (callbacks) {
        callbacks.resolve(undefined);
        this.callbackMap.delete(WorkerResponseType.Initialized);
      }
      return;
    }
    if (type === WorkerResponseType.Log) {
      const logLevel = e.data.logLevel;
      const logMessage = e.data.logMessage;
      zenidLog.log(logLevel, logMessage);
      return;
    }
    if (id === undefined) {
      //console.warn('Message received with no ID');
      return;
    }
    const callbacks = this.callbackMap.get(id);
    if (!callbacks) {
      zenidLog.warn(`No callback found for message with id: ${id}`);
      return;
    }
    const { resolve, reject } = callbacks;
    this.callbackMap.delete(id);
    if (type === WorkerResponseType.Error && error) {
      reject(new Error(error));
    } else if (type === WorkerResponseType.Result) {
      resolve(result);
    }
  }
  /**
   * Register callbacks for a specific call ID
   * @param {string|number} id - Call ID
   * @param {Function} resolve - Resolve function
   * @param {Function} reject - Reject function
   */
  registerCallback(id, resolve, reject) {
    this.callbackMap.set(id, { resolve, reject });
  }
  /**
   * Get a unique call ID
   * @returns {number} Unique call ID
   */
  getNextCallId() {
    return this.callIdCounter++;
  }
  /**
   * Process a frame
   * @param {string} feature - Feature name
   * @param {Uint8ClampedArray} imageData - Pointer to the input image
   * @param {number} width - Image width
   * @param {number} height - Image height
   * @returns {Promise<string>} - Processing result
   */
  processFrame(feature, imageData, width, height) {
    return this.callNative(
      NativeFunction.ProcessFrame,
      feature,
      imageData,
      width,
      height
    );
  }
  destroy() {
    zenidLog.info("Destroying ZenidSDK instance and cleaning up resources");
    try {
      // Terminate the worker
      if (this.worker) {
        this.worker.terminate();
        this.worker = null;
      }
      // Clear all pending callbacks
      this.callbackMap.clear();
      // Reset instance state
      this.initialized = false;
      this.initPromise = null;
      this.callIdCounter = 0;
      // Release any additional resources here if needed
    } catch (error) {
      zenidLog.error("Error during SDK cleanup:", error);
    }
  }
  /**
   * Kill all threads and workers
   * This is a utility method to forcefully terminate all resources
   * Especially useful for iOS WebKit memory.atomic.wait32 bug
   */
  killAll() {
    zenidLog.info("Forcefully killing all threads and workers");
    this.worker?.postMessage(new WorkerTerminateAction());
    zenidLog.info("All threads and workers have been terminated");
    this.initPromise = null;
    this.worker = null;
  }
  IsUsingSingleThreaded() {
    return this.useSingleThread === true;
  }
  // Convenience methods for common operations
  setVisualizerVersion(visualizerVersion) {
    return this.callNative(
      NativeFunction.SetVisualizerVersion,
      visualizerVersion
    );
  }
  initOrRestart(feature, jsSettings) {
    return this.callNative(NativeFunction.InitOrRestart, feature, jsSettings);
  }
  restartWithPreviousSettings(feature) {
    return this.callNative(NativeFunction.RestartWithPreviousSettings, feature);
  }
  getRenderCommands(feature, width, height) {
    return this.callNative(
      NativeFunction.GetRenderCommands,
      feature,
      width,
      height
    );
  }
  readyForFrame(feature) {
    return this.callNative(NativeFunction.ReadyForFrame, feature);
  }
  submitExternalResponse(feature, request) {
    return this.callNative(
      NativeFunction.SubmitExternalResponse,
      feature,
      request
    );
  }
  submitPhotoImage(feature, encodedImage) {
    const request = JSON.stringify(encodedImage);
    return this.callNative(
      NativeFunction.SubmitExternalResponse,
      feature,
      request
    );
  }
  /**
   * Get upload-ready data as a JSON string
   * @param {string} feature - Feature name
   * @returns {Promise<UploadReadyData>} - Parsed upload-ready data object
   */
  async getUploadReadyData(feature) {
    let jsonString = await this.callNative(
      NativeFunction.GetUploadReadyData,
      feature
    );
    return JSON.parse(jsonString);
  }
  async setDebugVisualization(isEnabled) {
    this.debugVisualizationEnabled = isEnabled;
    return this.callNative(NativeFunction.SetDebugVisualization, isEnabled);
  }
  setLanguage(language) {
    return this.callNative(NativeFunction.SetLanguage, language);
  }
  getLanguageLocale() {
    return this.callNative(NativeFunction.GetLanguageLocale);
  }
  authorize(responseToken) {
    return this.callNative(NativeFunction.Authorize, responseToken);
  }
  unloadAll() {
    return this.callNative(NativeFunction.Unload, "");
  }
  unloadFeature(feature) {
    return this.callNative(NativeFunction.Unload, feature);
  }
  getChallengeToken() {
    return this.callNative(NativeFunction.GetChallengeToken);
  }
  selectProfile(profileName) {
    return this.callNative(NativeFunction.SelectProfile, profileName);
  }
  getStateContainerJson(feature) {
    return this.callNative(NativeFunction.GetStateContainerJson, feature);
  }
  async getSdkWizardSteps() {
    let wizardStepsJson = await this.callNative(
      NativeFunction.GetSdkWizardSteps
    );
    return JSON.parse(wizardStepsJson);
  }
  getFrameProcessingResolution(feature) {
    return this.callNative(
      NativeFunction.GetFrameProcessingResolution,
      feature
    );
  }
  /**
   * Measure available RAM by attempting to allocate memory blocks
   * @returns {Promise<number>} Available RAM in bytes
   */
  measureAvailableRAM() {
    return this.callNative(NativeFunction.MeasureAvailableRAM);
  }
  /**
   * Gets the supported page count for a specific document code
   * @param documentCode - The document code value (from DocumentCodes enum)
   * @returns Number of supported pages (1 or 2)
   */
  getSupportedPageCount(documentCode) {
    return this.callNative(NativeFunction.GetSupportedPageCount, documentCode);
  }
}
// Export the SDK
export default ZenidSDK;
